import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import type {
  CostMarginId,
  CostTypeId,
  FragmentType,
} from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { PageViewer } from '../../components/PageViewer';
import {
  PATH_COST_GENERAL,
  PATH_COST_GENERAL_EDIT,
} from '../../app/constants/paths.constant';
import { CostForm } from '../../forms/Cost';
import { FormVariant } from '../../forms';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { Code } from '../../components/Code';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

import { VariableValue } from './VariableValue';
import { FixedValue } from './FixedValue';
import type { CostPage_VariableValue_VariableCost_Fragment } from './VariableValue';
import type { CostPage_FixedValue_FixedCost_Fragment } from './FixedValue';

export const CostPage_GeneralInfo_VariableCost_Fragment = graphql(`
  fragment CostPage_GeneralInfo_VariableCost_Fragment on VariableCost {
    _id
    codingConfig {
      kind
      strategyId
      ... on CodingConfigManual {
        code
      }
      ...CodeCmp_CodingConfig_Fragment
    }
    code
    label
    title
    content
    typeId
    costMarginId
    ...CostPage_VariableValue_VariableCost_Fragment
    ...CostForm_VariableCost_Fragment
  }
`);

export const CostPage_GeneralInfo_FixedCost_Fragment = graphql(`
  fragment CostPage_GeneralInfo_FixedCost_Fragment on FixedCost {
    _id
    codingConfig {
      kind
      strategyId
      ... on CodingConfigManual {
        code
      }
      ...CodeCmp_CodingConfig_Fragment
    }
    code
    label
    title
    content
    typeId
    costMarginId
    ...CostPage_FixedValue_FixedCost_Fragment
    ...CostForm_FixedCost_Fragment
  }
`);

interface Props {
  readonly fixedCost?: FragmentType<
    typeof CostPage_GeneralInfo_FixedCost_Fragment
  >;
  readonly variableCost?: FragmentType<
    typeof CostPage_GeneralInfo_VariableCost_Fragment
  >;
}

export const GeneralInfo = ({
  fixedCost: pFixedCost,
  variableCost: pVariableCost,
}: Readonly<Props>) => {
  const fixedCost = pFixedCost
    ? getFragmentData(CostPage_GeneralInfo_FixedCost_Fragment, pFixedCost)
    : undefined;
  const variableCost = pVariableCost
    ? getFragmentData(CostPage_GeneralInfo_VariableCost_Fragment, pVariableCost)
    : undefined;

  const { cost } = ensureDefined({ cost: fixedCost ?? variableCost });

  const { costTypesIdMap, costTypesCodeMap, costMarginsIdMap } =
    useReferencesContext();

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_COST_GENERAL,
      edit: PATH_COST_GENERAL_EDIT,
    },
    { id: cost._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const costTypeLabel =
    costTypesIdMap[cost.typeId as CostTypeId]?.label ?? 'Inconnu';

  const costMarginLabel =
    costMarginsIdMap[cost.costMarginId as CostMarginId]?.label ?? 'Inconnu';

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Typography gutterBottom variant="h6">
          Coût
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code code={cost.code} codingConfig={cost.codingConfig} />
            <Typography gutterBottom variant="body1">
              Libellé : {cost.label}
            </Typography>
            <Typography gutterBottom variant="body1">
              Type de coût : {costTypeLabel}
            </Typography>
            <Typography color="info.main" gutterBottom variant="body1">
              Valeur :{' '}
              {cost.typeId === costTypesCodeMap.VariableCost._id ? (
                <VariableValue
                  cost={
                    cost as FragmentType<
                      typeof CostPage_VariableValue_VariableCost_Fragment
                    >
                  }
                />
              ) : (
                <FixedValue
                  cost={
                    cost as FragmentType<
                      typeof CostPage_FixedValue_FixedCost_Fragment
                    >
                  }
                />
              )}
            </Typography>
            <Typography color="info.main" gutterBottom variant="body1">
              Marge sur coût : {costMarginLabel}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Document
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {/*eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing*/}
            {cost.title || (
              <Typography gutterBottom variant="body1">
                Titre non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {cost.content ? (
              <PageViewer html={cost.content} />
            ) : (
              <Typography gutterBottom variant="body1">
                Contenu non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editCostGeneralInfo',
            icon: <EditIcon />,
            name: 'Modifier Coût',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Générales du Coût"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <CostForm
          costId={cost._id}
          formVariant={FormVariant.Modification}
          initialFixedCostValues={fixedCost}
          initialVariableCostValues={variableCost}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          showCancelButton
          title="Formulaire de modification des Informations Générales du Coût"
        />
      </RightDrawer>
    </>
  );
};
